import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';

interface ForgotPasswordForm {
  email: string;
}

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ForgotPasswordForm>();

  const email = watch('email');

  const onSubmit = async (data: ForgotPasswordForm) => {
    setIsLoading(true);
    
    try {
      await api.post('/auth/forgot-password', data);
      setEmailSent(true);
      toast.success('Check your email for reset instructions');
    } catch (error: any) {
      const message = error.data?.message || 'Something went wrong. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="space-y-8">
        {/* Logo - mobile only */}
        <div className="lg:hidden text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-[#6B9F54] flex items-center justify-center shadow-sm">
              <span className="text-xl font-bold text-white">GG</span>
            </div>
            <span className="text-xl font-bold text-slate-900">Green Gold</span>
          </Link>
        </div>

        {/* Success message */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-6"
        >
          <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Check your email</h2>
            <p className="text-slate-600">
              We've sent a password reset link to
            </p>
            <p className="font-medium text-slate-900 mt-1">{email}</p>
          </div>

          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-sm text-slate-600">
            <p>Didn't receive the email? Check your spam folder or</p>
            <button
              onClick={() => setEmailSent(false)}
              className="text-[#6B9F54] hover:text-[#5A8C43] font-medium transition-colors"
            >
              try a different email address
            </button>
          </div>

          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#6B9F54] hover:text-[#5A8C43] transition-colors"
          >
            <ArrowLeft size={16} />
            Back to login
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Logo - mobile only */}
      <div className="lg:hidden text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-[#6B9F54] flex items-center justify-center shadow-sm">
            <span className="text-xl font-bold text-white">GG</span>
          </div>
          <span className="text-xl font-bold text-slate-900">Green Gold</span>
        </Link>
      </div>

      {/* Header */}
      <div className="text-center lg:text-left">
        <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">Forgot password?</h2>
        <p className="text-slate-600">
          No worries, we'll send you reset instructions.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <Mail size={18} />
            </div>
            <input
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              className={`w-full pl-11 pr-4 py-2.5 bg-white border rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                errors.email 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-[#6B9F54] focus:border-[#6B9F54]'
              }`}
              placeholder="you@example.com"
            />
          </div>
          {errors.email && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1.5 text-sm text-red-600"
            >
              {errors.email.message}
            </motion.p>
          )}
        </div>

        {/* Submit */}
        <motion.button
          type="submit"
          disabled={isLoading}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className="w-full py-3 px-4 bg-[#6B9F54] hover:bg-[#5A8C43] text-white rounded-lg font-semibold text-base transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm"
        >
          {isLoading ? (
            <Loader2 className="animate-spin" size={20} />
          ) : (
            <>
              Send Reset Link
              <ArrowRight size={18} />
            </>
          )}
        </motion.button>
      </form>

      {/* Footer */}
      <div className="text-center">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#6B9F54] hover:text-[#5A8C43] transition-colors"
        >
          <ArrowLeft size={16} />
          Back to login
        </Link>
      </div>
    </div>
  );
}
